import { Order, CardapioWebConfig, SystemConfig } from '../types';
import { dispatchCardapioOrder } from './cardapioWebService';

interface GhostDispatchServiceProps {
    orders: Order[];
    setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
    config: SystemConfig;
}

/**
 * v22.3: Smart Ghost Dispatch (Estratégia Anti-Penalização iFood)
 * 
 * Regra de Ouro: Desacopla o "Tempo de Plataforma" do "Tempo Logístico".
 * - Apenas DELIVERY + Marketplace (salesChannel === 'iFood').
 * - Virtualiza o status 'released' na API externa para proteger ranking.
 * - Mantém a flag 'ghostDispatch: true' para que a UI ignore atualizações de status externas.
 */
export const GhostDispatchService = (props: GhostDispatchServiceProps) => {
    const { orders, setOrders, config } = props;
    const cardapioWebConfig = config.cardapioWeb;

    const calculateGhostDispatchTriggerTime = (order: Order): number | null => {
        if (!cardapioWebConfig || !cardapioWebConfig.enableGhostDispatch) return null;

        const createdAt = order.createdAt;
        if (!createdAt) return null;

        const now = Date.now();

        if (cardapioWebConfig.ghostDispatchMode === 'dynamic') {
            const minSeconds = cardapioWebConfig.ghostDispatchDynamicMinSeconds || 60;
            const maxSeconds = cardapioWebConfig.ghostDispatchDynamicMaxSeconds || 300;
            const randomDelay = Math.floor(Math.random() * (maxSeconds - minSeconds + 1) + minSeconds) * 1000;
            return createdAt + randomDelay;
        } else if (cardapioWebConfig.ghostDispatchMode === 'predictive') {
            const bufferMinutes = cardapioWebConfig.ghostDispatchBufferMinutes || 5;
            const averageKitchenTimeMinutes = config.averageKitchenTime || 20;

            const estimatedDepartureTime = createdAt + averageKitchenTimeMinutes * 60 * 1000;
            const triggerTime = estimatedDepartureTime - bufferMinutes * 60 * 1000;

            if (triggerTime < now) {
                return now + Math.floor(Math.random() * 10) * 1000;
            }
            return triggerTime;
        }
        return null;
    };

    const monitorOrdersForGhostDispatch = async () => {
        if (!cardapioWebConfig?.active || !cardapioWebConfig?.enableGhostDispatch) return;

        const now = Date.now();
        // v22.3: Restrito a DELIVERY + iFood + FROTA PRÓPRIA EXPLICITAMENTE confirmada
        // Pedidos ainda não processados (undefined) NÃO são elegíveis
        const ordersToMonitor = orders.filter(order =>
            order.type === 'DELIVERY' &&
            order.salesChannel?.toLowerCase().includes('ifood') &&
            (order.status === 'confirmed' || order.status === 'ready') &&
            !order.ghostDispatchSent &&
            // CRITICAL: Apenas frota própria EXPLICITAMENTE confirmada:
            (order.optimizationOutcome === 'own' || order.manualAssignment === 'own') &&
            // Excluir explicitamente envios externos:
            order.optimizationOutcome !== 'third' &&
            order.manualAssignment !== 'third' &&
            !order.bestProvider &&
            !order.externalDeliveryId
        );

        const updatedOrders: Order[] = [];
        for (const order of ordersToMonitor) {
            let triggerTime = order.ghostDispatchTriggerTime;

            if (!triggerTime) {
                triggerTime = calculateGhostDispatchTriggerTime(order);
                if (triggerTime) {
                    updatedOrders.push({ ...order, ghostDispatchTriggerTime: triggerTime });
                }
            }

            if (triggerTime && now >= triggerTime) {
                try {
                    // Sinaliza para o PDV que o pedido "saiu"
                    const success = await dispatchCardapioOrder(cardapioWebConfig, order.platformId || order.id);

                    if (success) {
                        try {
                            const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2114/2114-preview.mp3');
                            audio.volume = 0.4;
                            audio.play().catch(e => console.warn("Ghost sound blocked by browser:", e));
                        } catch (e) { }

                        updatedOrders.push({
                            ...order,
                            ghostDispatch: true,
                            ghostDispatchSent: true,
                            ghostDispatchTriggerTime: triggerTime
                        });
                    }
                } catch (error) {
                    console.error(`[Ghost Dispatch Error] #${order.id}:`, error);
                }
            }
        }

        if (updatedOrders.length > 0) {
            setOrders(prevOrders => {
                const nextState = [...prevOrders];
                updatedOrders.forEach(updated => {
                    const idx = nextState.findIndex(o => o.id === updated.id);
                    if (idx !== -1) nextState[idx] = updated;
                });
                return nextState;
            });
        }
    };

    return { monitorOrdersForGhostDispatch };
};
